import { Textarea } from 'components/ui/textarea';
import useAuth from 'hooks/useAuth';
import { ReactNode, useId, useRef } from 'react';
import { handleAutofillOnClick, trimWithTrailingNewline } from 'utils/autofillUtil';
import { cn } from 'utils/cn';

interface Props {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    minRows?: number;
    maxRows?: number;
    disabled?: boolean;
}

/** `rows` lines of text plus the control's own vertical padding, in `em` — the form layer's textarea bounds. */
const rowsToEm = (rows: number) => `${(rows * 1.4375 + 1.15).toFixed(4)}em`;

/**
 * Handover / return notes textarea using the shared autofill helper: clicking into it stamps the
 * entry with "DD-MM-YYYY (User): " (with a leading blank line when the field starts empty) exactly
 * like every other autofilled notes field in the app, and blur trims stray whitespace. It grows with
 * its content from `minRows` up to `maxRows`, then scrolls.
 *
 * The label sits inside the outline, where the text starts, and floats onto the top border once the
 * field is focused or filled; the placeholder shows only while it is floated. It adds no height, so
 * the wizard's case rows keep their density.
 */
const HandoverNoteInput = ({
    value,
    onChange,
    label,
    placeholder,
    minRows = 3,
    maxRows,
    disabled,
}: Props): ReactNode => {
    const { user } = useAuth();
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const id = useId();

    return (
        <div className="relative">
            <Textarea
                ref={inputRef}
                id={id}
                className={cn(
                    'peer bg-paper min-h-0',
                    label && 'placeholder:opacity-0 focus-visible:placeholder:opacity-100'
                )}
                style={{ minHeight: rowsToEm(minRows), maxHeight: maxRows ? rowsToEm(maxRows) : undefined }}
                disabled={disabled}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onClick={() => handleAutofillOnClick(inputRef, value, user?.name, onChange)}
                onBlur={() => onChange(trimWithTrailingNewline(value))}
            />
            {label ? (
                <label
                    htmlFor={id}
                    className={cn(
                        'text-fg-secondary pointer-events-none absolute top-[9px] left-[7px] max-w-[calc(100%-14px)] truncate px-1 text-sm leading-5 transition-[top,font-size,line-height] duration-200 peer-disabled:opacity-50',
                        'peer-focus-visible:bg-paper peer-focus-visible:-top-2 peer-focus-visible:text-xs peer-focus-visible:leading-4',
                        value && 'bg-paper -top-2 text-xs leading-4'
                    )}
                >
                    {label}
                </label>
            ) : null}
        </div>
    );
};

export default HandoverNoteInput;
